import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../../identity/types';
import { jobRoleGateway } from '../../index';
import { jobRoleErrorMessage } from '../../jobRoleErrors';
import type { OccupationDataset, OccupationItem, OccupationSystem } from '../../types';
import type { ControlA11y } from '../../guidance/GuidedField';
import { GroupedCombobox, type ComboLoadResult } from './GroupedCombobox';

/**
 * Looks up an official occupation (SOC or O*NET) by title, reported title or code. The person always CHOOSES: the code is never typed in as free
 * text, never filled in for them, and a suggestion from the role title is only ever offered as "Suggested matches, please confirm".
 *
 *  - Every result shows the code and the official title, the major group, and which text matched.
 *  - The dataset (name, version, source, license and the attribution the publisher asks for) is shown beside the lookup.
 *  - After a SOC code is confirmed, O*NET results and suggestions are limited to that SOC.
 *  - "Not yet classified" (with a reason the parent asks for) is a legitimate answer. Choosing it clears the code.
 *  - When no dataset is loaded in this environment, an explanation is shown and "Not yet classified" is the way forward.
 *  - A code the role already has that is not in the current dataset is shown as "Saved value (not in the current dataset)" and is kept.
 */

export type ClassificationFormStatus = '' | 'Confirmed' | 'NotYetClassified';

export interface OccupationLookupProps {
  system: OccupationSystem;
  a11y: ControlA11y;
  /** The code on the role now ('' for none). */
  code: string;
  status: ClassificationFormStatus;
  /** The role title: used only to offer suggested matches for the person to confirm. */
  titleHint: string;
  /** For O*NET: the confirmed SOC code, which limits the results. */
  socCode?: string;
  disabled?: boolean;
  onPick: (item: OccupationItem) => void;
  onClear: () => void;
  onStatusChange: (status: ClassificationFormStatus) => void;
  /** Tells the parent what the current code is called (for the Review step), or null when it is not in the dataset. */
  onResolve?: (item: OccupationItem | null) => void;
}

type Resolved = { kind: 'none' } | { kind: 'found'; item: OccupationItem } | { kind: 'unknown' } | { kind: 'error' };

const systemName = (system: OccupationSystem) => (system === 'Soc' ? 'SOC' : 'O*NET');

function describe(item: OccupationItem): string {
  return [item.matchedOn === 'Synonym' && item.matchedText ? `Also known as “${item.matchedText}”` : null, item.majorGroupName].filter(Boolean).join(' · ');
}

function toOption(item: OccupationItem) {
  return { value: item.code, label: `${item.code} ${item.title}`, detail: describe(item) || null, meta: item.matchedOn === 'Code' ? 'Matched the code' : null };
}

export const NO_DATASET_TEXT =
  'The official occupation list has not been loaded in this environment. Choose “Not yet classified” for now; you can add the code later.';

export function OccupationLookup({ system, a11y, code, status, titleHint, socCode, disabled = false, onPick, onClear, onStatusChange, onResolve }: OccupationLookupProps) {
  const uid = useId();
  const [dataset, setDataset] = useState<OccupationDataset | null | undefined>(undefined);
  const [resolved, setResolved] = useState<Resolved>({ kind: 'none' });
  const [suggested, setSuggested] = useState<OccupationItem[]>([]);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const picked = useRef<OccupationItem | null>(null);
  const limit = system === 'Onet' && socCode ? socCode : undefined;
  const nyc = status === 'NotYetClassified';

  // The dataset in use, learned once (and again from every search). `null` means none is loaded.
  useEffect(() => {
    const controller = new AbortController();
    jobRoleGateway
      .searchOccupations({ system, take: 1 }, controller.signal)
      .then((r) => { if (!controller.signal.aborted) setDataset(r.dataset); })
      .catch(() => { if (!controller.signal.aborted) setDataset(undefined); });
    return () => controller.abort();
  }, [system]);

  // What the saved code is called. A code that is not in the dataset is shown and kept.
  useEffect(() => {
    if (!code) {
      picked.current = null;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResolved({ kind: 'none' });
      onResolve?.(null);
      return;
    }
    if (picked.current?.code === code) {
      setResolved({ kind: 'found', item: picked.current });
      onResolve?.(picked.current);
      return;
    }
    const controller = new AbortController();
    jobRoleGateway
      .getOccupation(system, code, controller.signal)
      .then((item) => { if (!controller.signal.aborted) { setResolved({ kind: 'found', item }); onResolve?.(item); } })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const notFound = err instanceof ApiError && err.status === 404;
        setResolved({ kind: notFound ? 'unknown' : 'error' });
        onResolve?.(null);
      });
    return () => controller.abort();
    // `onResolve` is a callback of the parent; only the code and the system decide what is looked up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, system]);

  // Suggested matches from the role title: offered, never chosen. Skipped once a code is set, or when "not yet classified" is chosen.
  const hint = titleHint.trim();
  useEffect(() => {
    if (code || nyc || disabled || (hint.length < 3 && !limit)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSuggested([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSuggestError(null);
      jobRoleGateway
        .searchOccupations({ system, search: hint.length >= 3 ? hint : undefined, take: 3, ...(limit ? { socCode: limit } : {}) }, controller.signal)
        .then(async (r) => {
          if (controller.signal.aborted) return;
          if (r.items.length === 0 && limit && hint.length >= 3) {
            // Nothing under that SOC matches the title: show what the SOC contains, so the choice is still limited to it.
            const all = await jobRoleGateway.searchOccupations({ system, take: 3, socCode: limit }, controller.signal);
            if (!controller.signal.aborted) setSuggested(all.items);
            return;
          }
          setSuggested(r.items);
        })
        .catch((err: unknown) => { if (!controller.signal.aborted) { setSuggested([]); setSuggestError(jobRoleErrorMessage(err, 'Could not look up suggestions.')); } });
    }, 400);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [system, hint, code, nyc, disabled, limit]);

  const load = useMemo(() => async (query: string, signal: AbortSignal): Promise<ComboLoadResult> => {
    const r = await jobRoleGateway.searchOccupations({ system, take: 20, ...(query ? { search: query } : {}), ...(limit ? { socCode: limit } : {}) }, signal);
    setDataset(r.dataset);
    return {
      groups: r.items.length === 0 ? [] : [{ key: 'matches', label: `${systemName(system)} occupations${limit ? ` under ${limit}` : ''}`, options: r.items.map(toOption) }],
      notice: r.totalMatches > r.items.length ? `Showing the first ${r.items.length} of ${r.totalMatches}. Type more of the title or the code to narrow it.` : null,
    };
  }, [system, limit]);

  const pick = (item: OccupationItem) => { picked.current = item; onPick(item); };

  const savedLabel = resolved.kind === 'found' ? `${resolved.item.code} ${resolved.item.title}` : code;
  const keptNote = code && resolved.kind === 'unknown' ? 'Saved value (not in the current dataset). It is kept until you change it.' : null;
  const socMismatch = system === 'Onet' && socCode && resolved.kind === 'found' && resolved.item.socCode && resolved.item.socCode !== socCode ? resolved.item.socCode : null;
  const noDataset = dataset === null;

  return (
    <div className="jr-occupation" data-testid={`occupation-lookup-${system.toLowerCase()}`}>
      <GroupedCombobox
        a11y={a11y}
        mode="select"
        value={code}
        valueLabel={savedLabel}
        disabled={disabled || nyc}
        load={load}
        clearLabel={`${systemName(system)} code`}
        onClear={onClear}
        onSelect={(option) => {
          // Re-read the full record so the description and group are real; the search result already carries them.
          void (async () => {
            try {
              const item = await jobRoleGateway.getOccupation(system, option.value);
              pick(item);
            } catch {
              pick({ code: option.value, title: option.label.replace(`${option.value} `, ''), description: null, majorGroupCode: null, majorGroupName: null, socCode: null, matchedOn: null, matchedText: null });
            }
          })();
        }}
        keptNote={keptNote}
        placeholder={nyc ? 'Marked not yet classified' : `Search the official ${systemName(system)} list by title or code`}
        emptyText={noDataset ? NO_DATASET_TEXT : 'No official occupation matches. Try another word, a reported job title, or the code.'}
      />

      {noDataset && <p className="jr-warning" data-testid="occupation-no-dataset">{NO_DATASET_TEXT}</p>}

      {code && resolved.kind === 'found' && (
        <dl className="jr-facts jr-facts--tight jr-occupation__facts" data-testid="occupation-selected">
          <div className="jr-facts__row"><dt>{systemName(system)} code</dt><dd>{resolved.item.code}</dd></div>
          <div className="jr-facts__row"><dt>Official title</dt><dd>{resolved.item.title}</dd></div>
          {resolved.item.majorGroupName && <div className="jr-facts__row"><dt>Major group</dt><dd>{resolved.item.majorGroupName}</dd></div>}
          {system === 'Onet' && resolved.item.socCode && <div className="jr-facts__row"><dt>SOC parent</dt><dd>{resolved.item.socCode}</dd></div>}
        </dl>
      )}
      {socMismatch && (
        <p className="jr-warning" data-testid="onet-soc-mismatch">
          <span aria-hidden="true">{'⚠'} </span>This O*NET occupation belongs to SOC {socMismatch}, but the SOC code on this role is {socCode}. You can keep both; check that is what you mean.
        </p>
      )}
      {code && resolved.kind === 'error' && <p className="jr-error" role="alert">Could not check this code against the official list. It is kept as it is.</p>}

      {suggested.length > 0 && (
        <div className="jr-occupation__suggest" role="group" aria-label="Suggested matches, please confirm">
          <p className="jr-suggestion__title">Suggested matches, please confirm</p>
          <p className="jr-hint">
            {limit ? `Limited to SOC ${limit}. ` : ''}Based on the role title. Nothing is chosen until you choose it.
          </p>
          <ul className="jr-plain-list jr-occupation__suggest-list">
            {suggested.map((s) => (
              <li key={s.code}>
                <span><strong>{s.code}</strong> {s.title}</span>
                {describe(s) && <span className="jr-hint"> {describe(s)}</span>}
                <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled} onClick={() => pick(s)}>
                  Use this{' '}<span className="jr-sr-only">{systemName(system)} occupation: {s.code} {s.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {suggestError && <p className="jr-hint">{suggestError}</p>}

      <label className="jr-occupation__nyc">
        <input
          type="checkbox" checked={nyc} disabled={disabled}
          onChange={(e) => onStatusChange(e.target.checked ? 'NotYetClassified' : '')}
        />
        {' '}Not yet classified
        {' '}<span className="jr-sr-only">({systemName(system)} code)</span>
      </label>

      {dataset && (
        <div className="jr-occupation__source" data-testid={`occupation-dataset-${system.toLowerCase()}`}>
          <p className="jr-hint">
            Official list: {dataset.name}, version <strong>{dataset.version}</strong>. Source: {dataset.sourceUrl
              ? <a href={dataset.sourceUrl} target="_blank" rel="noopener noreferrer">{dataset.sourceOrganization}{' '}<span className="jr-sr-only">(opens in a new tab)</span></a>
              : dataset.sourceOrganization}
            {dataset.licenseName ? `. License: ${dataset.licenseName}` : ''}.
          </p>
          <details className="jr-occupation__details" id={`${uid}-attr`}>
            <summary>Source, license and limits of this list</summary>
            {dataset.attributionText && <p className="jr-hint">{dataset.attributionText}</p>}
            {dataset.limitation && <p className="jr-hint">Limit: {dataset.limitation}</p>}
            {dataset.licenseUrl && <p className="jr-hint"><a href={dataset.licenseUrl} target="_blank" rel="noopener noreferrer">License terms{' '}<span className="jr-sr-only">(opens in a new tab)</span></a></p>}
          </details>
        </div>
      )}
    </div>
  );
}
